import React, { useState, useCallback, useEffect } from "react";
import { Popover, Button, Space } from "antd";
import { DeleteOutlined, ClearOutlined, EnterOutlined } from "@ant-design/icons";
import styles from "./NumericKeyboard.module.css";

/**
 * Props for the NumericKeyboard component
 */
export interface NumericKeyboardProps {
  /** Title displayed at the top of the keyboard popover */
  title: string;
  /** Optional unit suffix to display after the value (e.g., "kg", "ks") */
  unit?: string;
  /** Current numeric value */
  value: number;
  /** Callback when the value changes */
  onChange: (value: number) => void;
  /** Callback when Enter is pressed */
  onEnter: () => void;
  /** The trigger element (button/input) that opens the keyboard */
  children: React.ReactNode;
  /** Optional controlled visibility */
  visible?: boolean;
  /** Optional callback when visibility changes */
  onVisibleChange?: (visible: boolean) => void;
}

/**
 * NumericKeyboard - A virtual numeric keyboard component for touch-friendly numeric input
 *
 * Features:
 * - 3x4 grid layout with digits 0-9
 * - Decimal point support (accepts both comma and dot)
 * - Backspace, Clear, and Enter actions
 * - Displays current value with optional unit suffix
 * - Locale-aware decimal handling (comma converted to dot internally)
 *
 * @example
 * ```tsx
 * <NumericKeyboard
 *   title="Enter quantity"
 *   unit="ks"
 *   value={quantity}
 *   onChange={setQuantity}
 *   onEnter={handleSubmit}
 * >
 *   <Button>Enter Quantity</Button>
 * </NumericKeyboard>
 * ```
 */
export const NumericKeyboard: React.FC<NumericKeyboardProps> = ({
  title,
  unit,
  value,
  onChange,
  onEnter,
  children,
  visible,
  onVisibleChange,
}) => {
  // Internal string representation of the input for editing
  const [inputText, setInputText] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  // Sync inputText with value prop when popover opens or value changes externally
  useEffect(() => {
    if (isOpen || visible) {
      const valueStr = value.toString().replace(".", ",");
      setInputText(valueStr);
    }
  }, [value, isOpen, visible]);

  // Handle visibility state
  const handleVisibleChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onVisibleChange?.(open);
    },
    [onVisibleChange]
  );

  // Convert input text to number (handle comma as decimal separator)
  const parseInputValue = useCallback((text: string): number => {
    if (!text || text === "" || text === "," || text === ".") {
      return 0;
    }
    // Replace comma with dot for parsing
    const normalized = text.replace(",", ".");
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  }, []);

  // Handle digit/comma button clicks
  const handleInput = useCallback(
    (char: string) => {
      let newText = inputText + char;

      // Prevent multiple decimal separators
      if ((char === "," || char === ".") && (inputText.includes(",") || inputText.includes("."))) {
        return;
      }

      // Normalize dot to comma for display (Slovak locale)
      if (char === ".") {
        newText = inputText + ",";
      }

      setInputText(newText);
      const numericValue = parseInputValue(newText);
      onChange(numericValue);
    },
    [inputText, onChange, parseInputValue]
  );

  // Handle backspace
  const handleBackspace = useCallback(() => {
    const newText = inputText.slice(0, -1);
    setInputText(newText);
    const numericValue = parseInputValue(newText);
    onChange(numericValue);
  }, [inputText, onChange, parseInputValue]);

  // Handle clear
  const handleClear = useCallback(() => {
    setInputText("");
    onChange(0);
  }, [onChange]);

  // Handle enter
  const handleEnter = useCallback(() => {
    onEnter();
    handleVisibleChange(false);
  }, [onEnter, handleVisibleChange]);

  // Handle hardware keyboard input when popover is open
  useEffect(() => {
    const actuallyOpen = visible !== undefined ? visible : isOpen;
    if (!actuallyOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in another input field
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;

      // Digits 0-9
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        handleInput(e.key);
        return;
      }

      // Decimal separator (comma or dot)
      if (e.key === "," || e.key === ".") {
        e.preventDefault();
        handleInput(",");
        return;
      }

      // Backspace
      if (e.key === "Backspace") {
        e.preventDefault();
        handleBackspace();
        return;
      }

      // Enter
      if (e.key === "Enter") {
        e.preventDefault();
        handleEnter();
        return;
      }

      // Escape - close keyboard
      if (e.key === "Escape") {
        e.preventDefault();
        handleVisibleChange(false);
        return;
      }

      // Delete - clear
      if (e.key === "Delete") {
        e.preventDefault();
        handleClear();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, isOpen, handleInput, handleBackspace, handleEnter, handleClear, handleVisibleChange]);

  // Keyboard content
  const keyboardContent = (
    <div className={styles.keyboardContainer}>
      <div className={styles.display}>
        <span className={styles.value}>
          {inputText || "0"}
          {unit && <span className={styles.unit}>{unit}</span>}
        </span>
      </div>
      <div className={styles.buttonGrid}>
        {/* Digits 1-9 in 3x3 grid */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
          <Button
            key={digit}
            className={styles.digitButton}
            onClick={() => handleInput(digit.toString())}
            size="large"
          >
            {digit}
          </Button>
        ))}
        {/* Bottom row: comma, 0, backspace */}
        <Button
          className={styles.digitButton}
          onClick={() => handleInput(",")}
          size="large"
        >
          ,
        </Button>
        <Button
          className={styles.digitButton}
          onClick={() => handleInput("0")}
          size="large"
        >
          0
        </Button>
        <Button
          className={styles.digitButton}
          onClick={handleBackspace}
          size="large"
          icon={<DeleteOutlined />}
        />
      </div>
      <Space className={styles.actionButtons} size="small">
        <Button
          danger
          onClick={handleClear}
          icon={<ClearOutlined />}
          size="large"
        >
          Clear
        </Button>
        <Button
          type="primary"
          onClick={handleEnter}
          icon={<EnterOutlined />}
          size="large"
        >
          ENTER
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover
      content={keyboardContent}
      title={title}
      trigger="click"
      visible={visible !== undefined ? visible : isOpen}
      onVisibleChange={handleVisibleChange}
      placement="bottom"
      overlayClassName={styles.keyboardPopover}
    >
      {children}
    </Popover>
  );
};
